/* Tech Sofa: header drawer. The burger toggles .is-open on the header; any link tap closes it. */
if (!customElements.get('ts-header')) {
  customElements.define('ts-header', class TsHeader extends HTMLElement {
    connectedCallback() {
      this.burger = this.querySelector('[data-burger]');
      if (!this.burger || this.ready) return;
      this.ready = true;
      this.openLabel = this.burger.dataset.openLabel || 'Open menu';
      this.closeLabel = this.burger.dataset.closeLabel || 'Close menu';
      this.burger.addEventListener('click', () => this.toggle(!this.classList.contains('is-open')));
      this.querySelectorAll('[data-drawer] a').forEach((a) => a.addEventListener('click', () => this.toggle(false)));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) { this.toggle(false); this.burger.focus(); }
      });
      /* the open menu covers the page, so focus must not wander underneath it */
      this.addEventListener('focusout', (e) => {
        if (this.classList.contains('is-open') && e.relatedTarget && !this.contains(e.relatedTarget)) this.toggle(false);
      });
      /* growing the window past the burger breakpoint closes the menu and releases the page */
      this.desktop = window.matchMedia('(min-width: 1041px)');
      this.onDesktop = (e) => { if (e.matches && this.classList.contains('is-open')) this.toggle(false); };
      this.desktop.addEventListener('change', this.onDesktop);
    }

    disconnectedCallback() {
      if (this.desktop) this.desktop.removeEventListener('change', this.onDesktop);
      document.body.classList.remove('overflow-hidden');
      this.ready = false;
    }

    toggle(open) {
      if (open) {
        const bar = this.querySelector('.ts-head__in');
        this.style.setProperty('--ts-drawer-top', Math.round(bar.getBoundingClientRect().bottom) + 'px');
      }
      document.body.classList.toggle('overflow-hidden', open);
      this.classList.toggle('is-open', open);
      this.burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.burger.setAttribute('aria-label', open ? this.closeLabel : this.openLabel);
    }
  });
}

/* Predictive search for the new header. It extends the theme's own <predictive-search> class (assets/predictive-search.js,
   loaded when predictive search is switched on in theme settings) rather than duplicating it. Two things differ:
   the results come from the predictive-search-new section, and the available height is measured from the new header
   (the theme's version looks for #shopify-section-header, which the new layout no longer has).
   If predictive search is switched off the base class never loads and the field stays a plain search form. */
customElements.whenDefined('predictive-search').then(() => {
  if (customElements.get('ts-predictive-search')) return;
  const Base = customElements.get('predictive-search');

  customElements.define('ts-predictive-search', class TsPredictiveSearch extends Base {
    constructor() {
      super();
      this.addEventListener('click', (e) => {
        if (!e.target.closest('[data-ts-search-term]')) return;
        const form = this.querySelector('form');
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
      });
      this.addEventListener('keyup', (e) => { if (e.code === 'Escape') this.close(); });
    }

    getResultsMaxHeight() {
      const head = this.closest('ts-header');
      const bottom = head ? head.querySelector('.ts-head__in').getBoundingClientRect().bottom : this.getBoundingClientRect().bottom;
      this.resultsMaxHeight = Math.max(240, window.innerHeight - bottom - 24);
      return this.resultsMaxHeight;
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      const params = new URLSearchParams({ q: searchTerm, 'resources[type]': 'product', 'resources[limit]': '4', section_id: 'predictive-search-new' });
      fetch(`${routes.predictive_search_url}?${params.toString()}`)
        .then((response) => {
          if (!response.ok) { this.close(); throw new Error(response.status); }
          return response.text();
        })
        .then((text) => {
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search-new');
          if (!section) { this.close(); return; }
          this.cachedResults[queryKey] = section.innerHTML;
          /* ignore a response that arrives after the field has been cleared or changed */
          if (this.getQuery() === searchTerm) this.renderSearchResults(section.innerHTML);
        })
        .catch((error) => { this.close(); throw error; });
    }
  });
});
